/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type AppointmentModuleEmailContentsSpecsFragment } from '../../../../api/fragments/AppointmentModuleEmailContentsSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteAppointmentModuleAssetDocument,
    type DeleteAppointmentModuleAssetMutation,
    type DeleteAppointmentModuleAssetMutationVariables,
} from '../../../../api/mutations/deleteAppointmentModuleAsset';
import {
    UploadAppointmentModuleAssetDocument,
    type UploadAppointmentModuleAssetMutationVariables,
    type UploadAppointmentModuleAssetMutation,
} from '../../../../api/mutations/uploadAppointmentModuleAsset';
import { AppointmentModuleAsset } from '../../../../api/types';
import { type AppointmentEmailFormValues } from './shared';

type UploadParams = {
    moduleId: string;
    upload: File | UploadFileWithPreviewFormDataFragment;
    type: AppointmentModuleAsset;
};

const useHandleAppointmentModuleAssets = () => {
    const apolloClient = useApolloClient();

    const updateOrDelete = useCallback(
        ({ type, moduleId, upload }: UploadParams, initialValue: UploadFileWithPreviewFormDataFragment) => {
            const uploads = [];

            if (upload instanceof File) {
                // upload a new file
                uploads.push(
                    apolloClient.mutate<
                        UploadAppointmentModuleAssetMutation,
                        UploadAppointmentModuleAssetMutationVariables
                    >({
                        mutation: UploadAppointmentModuleAssetDocument,
                        variables: {
                            moduleId,
                            type,
                            upload,
                        },
                    })
                );
            } else if (upload === null && isObject(initialValue)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteAppointmentModuleAssetMutation,
                        DeleteAppointmentModuleAssetMutationVariables
                    >({
                        mutation: DeleteAppointmentModuleAssetDocument,
                        variables: { moduleId, type },
                    })
                );
            }

            return uploads;
        },
        [apolloClient]
    );

    return useCallback(
        (
            moduleId: string,
            values: AppointmentEmailFormValues,
            initialValues?: AppointmentModuleEmailContentsSpecsFragment
        ) => {
            const { customer, salesPerson } = values;

            const uploads = [
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: customer.endTestDrive.introImage,
                        type: AppointmentModuleAsset.CustomerEndTestDrive,
                    },
                    initialValues?.customer.endTestDrive.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: customer.submitConfirmation.introImage,
                        type: AppointmentModuleAsset.CustomerSubmitConfirmation,
                    },
                    initialValues?.customer.submitConfirmation.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: customer.bookingAmendment.introImage,
                        type: AppointmentModuleAsset.CustomerBookingAmendment,
                    },
                    initialValues?.customer.bookingAmendment.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: customer.bookingConfirmation.introImage,
                        type: AppointmentModuleAsset.CustomerBookingConfirmation,
                    },
                    initialValues?.customer.bookingConfirmation.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: salesPerson.submitConfirmation.introImage,
                        type: AppointmentModuleAsset.SalesPersonSubmitConfirmation,
                    },
                    initialValues?.salesPerson.submitConfirmation.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: salesPerson.finderReservation.introImage,
                        type: AppointmentModuleAsset.SalesPersonFinderReservation,
                    },
                    initialValues?.salesPerson.finderReservation.introImage
                ),
            ];

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [updateOrDelete]
    );
};

export default useHandleAppointmentModuleAssets;
